"use client"

import { useState, useEffect, useRef } from "react"
import { format } from "date-fns"
import { Bar } from "react-chartjs-2"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js"
import { FiX, FiDownload, FiDollarSign, FiLayers, FiCalendar, FiShield, FiFolder, FiPieChart } from "react-icons/fi"
import { FaSpinner } from "react-icons/fa"
import { jsPDF } from "jspdf"
import html2canvas from "html2canvas"

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const formatCurrency = (value) => {
  const val = Math.max(Math.round(Number(value || 0)), 0)
  return `Rp ${val.toLocaleString("id-ID")}`
}

const formatDate = (dateString) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`
}

const getPreviousMonth = (month) => {
  if (!month || typeof month !== "string") return ""
  const [year, monthIndex] = month.split("-").map(Number)
  return format(new Date(year, monthIndex - 2, 1), "yyyy-MM")
}

const isCurrentMonth = (month) => {
  const current = format(new Date(), "yyyy-MM")
  return !month || month >= current
}

const serviceColors = {
  "Compute Engine": "#0284c7",
  "Cloud SQL": "#2563eb",
  Networking: "#f59e0b",
  "Maps API": "#10b981",
  "Cloud Storage": "#ea580c",
  "Geocoding API": "#8b5cf6",
  "Places API": "#ec4899",
  "Places API (New)": "#06b6d4",
  "VM Manager": "#64748b",
  "Firebase Hosting": "#f97316",
  "Cloud Monitoring": "#ef4444",
  "Firebase Realtime Database": "#eab308",
}

const getServiceColor = (serviceName, index) => {
  return serviceColors[serviceName] || `hsl(${(index * 360) / 12}, 70%, 50%)`
}

const savingsKeys = [
  "negotiated_savings",
  "spend_based_cud_savings",
  "legacy_cud_savings",
  "free_tier_savings",
  "promotional_savings",
  "sud_savings",
  "spending_based_savings",
]

export default function UsageReportPDF({
  isOpen,
  onClose,
  selectedMonth,
  selectedProject,
  selectedProjects = [],
  selectedBillingId,
  customerName = "",
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [reportPayload, setReportPayload] = useState(null)
  const [activeTab, setActiveTab] = useState("summary")
  const [logoBase64, setLogoBase64] = useState("")
  const previewChartRef = useRef(null)

  // Resolve Customer Name from prop or localStorage
  const resolvedCustomerName =
    customerName ||
    (typeof window !== "undefined"
      ? (() => {
          try {
            return JSON.parse(localStorage.getItem("user") || "{}")?.company_name || ""
          } catch {
            return ""
          }
        })()
      : "") ||
    ""

  // Compute effective project list
  const effectiveProjects =
    selectedProjects && selectedProjects.length > 0
      ? selectedProjects
      : selectedProject
      ? [selectedProject]
      : []

  useEffect(() => {
    // Try to load logo as base64 or fallback to origin + /logo.png
    if (typeof window !== "undefined") {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas")
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext("2d")
          ctx.drawImage(img, 0, 0)
          setLogoBase64(canvas.toDataURL("image/png"))
        } catch {
          setLogoBase64(window.location.origin + "/logo.png")
        }
      }
      img.onerror = () => {
        setLogoBase64(window.location.origin + "/logo.png")
      }
      img.src = "/logo.png"
    }
  }, [])

  useEffect(() => {
    if (isOpen && selectedMonth) {
      setActiveTab("summary")
      fetchBillingData()
    }
  }, [isOpen, selectedMonth, selectedProject, selectedProjects, selectedBillingId])

  const fetchBillingData = async () => {
    setIsLoading(true)
    try {
      const billingAccountId = selectedBillingId || "01E318-E504D7-6A5BBF"
      const prevMonth = getPreviousMonth(selectedMonth)

      const currentParams = new URLSearchParams({
        billing_account_id: billingAccountId,
        year_month: `${selectedMonth}-01`,
      })
      if (effectiveProjects.length > 0) {
        currentParams.set("project_ids", effectiveProjects.join(","))
      }

      const prevParams = new URLSearchParams({
        billing_account_id: billingAccountId,
        year_month: `${prevMonth}-01`,
      })
      if (effectiveProjects.length > 0) {
        prevParams.set("project_ids", effectiveProjects.join(","))
      }

      const [currentRes, prevRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/billing/billing-by-service-by-project?${currentParams}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
        ),
        fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/billing/billing-by-service-by-project?${prevParams}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
        ).catch(() => null),
      ])

      if (!currentRes.ok) {
        throw new Error(`API request failed with status ${currentRes.status}`)
      }

      const currentData = await currentRes.json()
      const prevData = prevRes && prevRes.ok ? await prevRes.json() : null

      processAllBillingData(currentData, prevData)
    } catch (error) {
      console.error("Error fetching billing data for report:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const extractMonthData = (data, monthPrefix, filterProjectId = null) => {
    const grouped = data?.["billing by service and project data"] || {}
    const serviceTotals = {}
    const grossTotals = {}
    const savingsTotals = {}
    const dailyData = []

    const dates = Object.keys(grouped)
      .filter((d) => !monthPrefix || d.startsWith(monthPrefix))
      .sort()

    dates.forEach((date) => {
      let rows = grouped[date] || []
      if (filterProjectId) {
        rows = rows.filter((r) => (r.project_id || "") === filterProjectId)
      }
      let dayTotal = 0
      const dayServices = {}

      rows.forEach((service) => {
        const { service_name, subtotal } = service
        if (!service_name || service_name.toLowerCase() === "invoice") return

        const gross = Number(subtotal || 0)
        const savings = savingsKeys.reduce((sum, key) => sum + Number(service[key] || 0), 0)
        const net = Math.max(gross + savings, 0)

        if (!serviceTotals[service_name]) serviceTotals[service_name] = 0
        if (!grossTotals[service_name]) grossTotals[service_name] = 0
        if (!savingsTotals[service_name]) savingsTotals[service_name] = 0

        serviceTotals[service_name] += net
        grossTotals[service_name] += gross
        savingsTotals[service_name] += savings

        dayTotal += net
        dayServices[service_name] = (dayServices[service_name] || 0) + net
      })

      dailyData.push({
        date,
        formattedDate: formatDate(date),
        total: Math.max(dayTotal, 0),
        services: dayServices,
      })
    })

    const totalCost = Object.values(serviceTotals).reduce((sum, c) => sum + Number(c), 0)
    const totalGross = Object.values(grossTotals).reduce((sum, c) => sum + Number(c), 0)
    const totalSavings = Math.abs(Object.values(savingsTotals).reduce((sum, c) => sum + Number(c), 0))

    return {
      serviceTotals,
      grossTotals,
      savingsTotals,
      dailyData,
      totalCost,
      totalGross,
      totalSavings,
      dates,
    }
  }

  const buildPageData = (currentData, prevData, projectId = null, pageTitle = null) => {
    const current = extractMonthData(currentData, selectedMonth, projectId)
    const prevMonth = getPreviousMonth(selectedMonth)
    const prev = prevData ? extractMonthData(prevData, prevMonth, projectId) : null

    const isOngoing = isCurrentMonth(selectedMonth)
    let overallChangePercent = null
    let overallDelta = null

    if (!isOngoing && prev && prev.totalCost > 0) {
      overallChangePercent = ((current.totalCost - prev.totalCost) / prev.totalCost) * 100
      overallDelta = current.totalCost - prev.totalCost
    }

    const allServices = Array.from(
      new Set([
        ...Object.keys(current.serviceTotals),
        ...(prev ? Object.keys(prev.serviceTotals) : []),
      ]),
    )

    const serviceBreakdown = allServices.map((name) => {
      const cost = Math.max(current.serviceTotals[name] || 0, 0)
      const prevCost = prev && prev.serviceTotals[name] !== undefined ? Math.max(prev.serviceTotals[name], 0) : null
      const share = current.totalCost > 0 ? (cost / current.totalCost) * 100 : 0

      let changePercent = null
      let isNew = false
      if (!isOngoing && prevCost !== null) {
        if (prevCost > 0) {
          changePercent = ((cost - prevCost) / prevCost) * 100
        } else if (cost > 0) {
          isNew = true
        } else {
          changePercent = 0
        }
      }

      return {
        name,
        cost,
        prevCost,
        share,
        changePercent,
        isNew,
      }
    })

    // Sort descending by current cost
    serviceBreakdown.sort((a, b) => b.cost - a.cost || (b.prevCost || 0) - (a.prevCost || 0))

    const activeServices = serviceBreakdown.filter((s) => s.cost > 0)

    const dailyChartData = {
      labels: current.dailyData.map((d) => d.formattedDate),
      datasets: activeServices.map((s, index) => ({
        label: s.name,
        data: current.dailyData.map((d) => Math.round(d.services?.[s.name] || 0)),
        backgroundColor: getServiceColor(s.name, index),
        borderColor: getServiceColor(s.name, index),
        borderWidth: 0.5,
        stack: "stack1",
      })),
    }

    const topService = serviceBreakdown.length > 0 ? serviceBreakdown[0] : null
    const activeServicesCount = activeServices.length

    return {
      projectId,
      pageTitle: pageTitle || (projectId ? `Project: ${projectId}` : "Combined Projects Summary"),
      serviceBreakdown,
      totalCost: current.totalCost,
      totalGross: current.totalGross,
      totalSavings: current.totalSavings,
      prevTotalCost: prev?.totalCost || null,
      overallChangePercent,
      overallDelta,
      serviceCount: serviceBreakdown.length,
      activeServicesCount,
      topService,
      dateRange: current.dates.length
        ? `${formatDate(current.dates[0])} - ${formatDate(current.dates[current.dates.length - 1])} ${new Date(current.dates[current.dates.length - 1]).getFullYear()}`
        : "",
      dailyChartData,
      isOngoing,
      prevMonthLabel: prevMonth ? format(new Date(`${prevMonth}-01`), "MMM yyyy") : "Prev Month",
      currMonthLabel: format(new Date(`${selectedMonth}-01`), "MMM yyyy"),
    }
  }

  const processAllBillingData = (currentData, prevData) => {
    if (!currentData || !currentData["billing by service and project data"]) {
      setReportPayload(null)
      return
    }

    if (effectiveProjects.length >= 2) {
      // Multi-project mode: Page per project + Combined Summary
      const projectPages = effectiveProjects.map((proj) =>
        buildPageData(currentData, prevData, proj, `Project: ${proj}`)
      )
      const summaryPage = buildPageData(
        currentData,
        prevData,
        null,
        `Combined Projects Summary (${effectiveProjects.length} Projects)`
      )

      // Project contribution stats for summary page
      const projectContributions = effectiveProjects
        .map((proj) => {
          const pData = projectPages.find((p) => p.projectId === proj)
          const cost = pData ? pData.totalCost : 0
          const share = summaryPage.totalCost > 0 ? (cost / summaryPage.totalCost) * 100 : 0
          return { projectId: proj, cost, share }
        })
        .sort((a, b) => b.cost - a.cost)

      setReportPayload({
        isMultiProject: true,
        projectPages,
        summaryPage,
        projectContributions,
        // Print order: Page A, Page B, ..., Summary Gabungan (A + B + ...)
        allPages: [...projectPages, summaryPage],
      })
    } else if (effectiveProjects.length === 1) {
      // Single project mode
      const singlePage = buildPageData(
        currentData,
        prevData,
        effectiveProjects[0],
        `Project: ${effectiveProjects[0]}`
      )
      setReportPayload({
        isMultiProject: false,
        projectPages: [singlePage],
        summaryPage: null,
        projectContributions: [],
        allPages: [singlePage],
      })
    } else {
      // Default: Entire Billing Account
      const entireAccountPage = buildPageData(
        currentData,
        prevData,
        null,
        "Entire Billing Account"
      )
      setReportPayload({
        isMultiProject: false,
        projectPages: [],
        summaryPage: entireAccountPage,
        projectContributions: [],
        allPages: [entireAccountPage],
      })
    }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "end",
        labels: {
          boxWidth: 7,
          boxHeight: 7,
          usePointStyle: true,
          pointStyle: "circle",
          padding: 8,
          font: { size: 9, weight: "600" },
          color: "#475569",
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatCurrency(context.raw)}`,
          footer: (tooltipItems) => {
            const sum = tooltipItems.reduce((acc, item) => acc + (Number(item.raw) || 0), 0)
            return `Daily Total: ${formatCurrency(sum)}`
          },
        },
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        footerColor: "#93c5fd",
        padding: 8,
        cornerRadius: 6,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { size: 9.5, weight: "500" },
          color: "#64748b",
          autoSkip: true,
          maxTicksLimit: 15,
        },
        border: { color: "#e2e8f0" },
      },
      y: {
        stacked: true,
        ticks: {
          callback: (value) => (value >= 1000000 ? `Rp${(value / 1000000).toFixed(1)}M` : `Rp${(value / 1000).toFixed(0)}K`),
          font: { size: 9.5, weight: "500" },
          color: "#64748b",
          padding: 6,
        },
        grid: {
          color: "rgba(226, 232, 240, 0.7)",
          lineWidth: 1,
        },
        border: { color: "#e2e8f0" },
      },
    },
    elements: {
      bar: { borderRadius: 0 },
    },
  }

  // Generate crisp offscreen chart base64 image for printed PDF
  const renderChartToBase64 = (dailyChartData) => {
    if (!dailyChartData || !dailyChartData.datasets || dailyChartData.datasets.length === 0) {
      return ""
    }
    try {
      const canvas = document.createElement("canvas")
      canvas.width = 1100
      canvas.height = 280
      const ctx = canvas.getContext("2d")
      const chart = new ChartJS(ctx, {
        type: "bar",
        data: dailyChartData,
        options: {
          ...chartOptions,
          animation: false,
          responsive: false,
        },
      })
      const imageBase64 = chart.toBase64Image()
      chart.destroy()
      return imageBase64
    } catch (err) {
      console.warn("Failed to generate offscreen chart base64:", err)
      return ""
    }
  }

  // Generate HTML block for a single report page
  const generatePageHTML = (
    pageData,
    chartImg,
    isSummaryPage = false,
    pageNumber = 1,
    totalPages = 1,
    contributions = []
  ) => {
    const monthYear = format(new Date(`${selectedMonth}-01`), "MMMM yyyy")
    const generatedTimestamp = `${new Date().toLocaleDateString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })}, ${new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} WIB`

    const momBadgeOverall =
      pageData.overallChangePercent !== null
        ? pageData.overallChangePercent > 0
          ? `<span class="badge-up">↑ ${Math.abs(Math.round(pageData.overallChangePercent))}% (+${formatCurrency(pageData.overallDelta)})</span>`
          : `<span class="badge-down">↓ ${Math.abs(Math.round(pageData.overallChangePercent))}% (-${formatCurrency(Math.abs(pageData.overallDelta))})</span>`
        : `<span class="badge-neutral">Ongoing (${pageData.dateRange})</span>`

    // Summary Insights
    let summarySentence = ""
    if (pageData.isOngoing) {
      summarySentence = `Periode ini masih berjalan (${pageData.dateRange}). Akumulasi pengeluaran cloud hingga saat ini tercatat sebesar <strong>${formatCurrency(pageData.totalCost)}</strong>.`
    } else if (pageData.overallChangePercent !== null) {
      if (pageData.overallChangePercent > 0) {
        summarySentence = `Total pengeluaran cloud periode ini sebesar <strong>${formatCurrency(pageData.totalCost)}</strong>, mengalami kenaikan sebesar <strong>${formatCurrency(pageData.overallDelta)} (↑ ${Math.abs(Math.round(pageData.overallChangePercent))}%)</strong> dibandingkan ${pageData.prevMonthLabel}.`
      } else if (pageData.overallChangePercent < 0) {
        summarySentence = `Total pengeluaran cloud periode ini sebesar <strong>${formatCurrency(pageData.totalCost)}</strong>, berhasil mengalami penghematan/penurunan biaya sebesar <strong>${formatCurrency(Math.abs(pageData.overallDelta))} (↓ ${Math.abs(Math.round(pageData.overallChangePercent))}%)</strong> dibandingkan ${pageData.prevMonthLabel}.`
      } else {
        summarySentence = `Total pengeluaran cloud periode ini stabil sebesar <strong>${formatCurrency(pageData.totalCost)}</strong>, setara dengan ${pageData.prevMonthLabel}.`
      }
    } else {
      summarySentence = `Total pengeluaran cloud periode ini tercatat sebesar <strong>${formatCurrency(pageData.totalCost)}</strong>.`
    }

    const topIncreases = pageData.serviceBreakdown
      .filter((s) => s.prevCost !== null && s.cost > s.prevCost && s.cost - s.prevCost > 50000)
      .sort((a, b) => (b.cost - b.prevCost) - (a.cost - a.prevCost))

    const topDecreases = pageData.serviceBreakdown
      .filter((s) => s.prevCost !== null && s.cost < s.prevCost && s.prevCost - s.cost > 50000)
      .sort((a, b) => (b.prevCost - b.cost) - (a.prevCost - a.cost))

    const highlightItems = []
    if (topIncreases.length > 0) {
      const incStr = topIncreases.slice(0, 2).map((s) => `<strong>${s.name}</strong> (+${formatCurrency(s.cost - s.prevCost)}, ↑${Math.round(s.changePercent)}%)`).join(" dan ")
      highlightItems.push(`Kenaikan biaya tercatat pada ${incStr}.`)
    }
    if (topDecreases.length > 0) {
      const decStr = topDecreases.slice(0, 2).map((s) => `<strong>${s.name}</strong> (hemat ${formatCurrency(s.prevCost - s.cost)}, ↓${Math.abs(Math.round(s.changePercent))}%)`).join(" dan ")
      highlightItems.push(`Efisiensi biaya berhasil dicapai pada ${decStr}.`)
    }
    const highlightText = highlightItems.join(" ")

    const tableRows = pageData.serviceBreakdown
      .map((item, idx) => {
        let badge = '<span class="badge-neutral">—</span>'
        if (item.isNew) {
          badge = '<span class="badge-new">NEW</span>'
        } else if (item.changePercent !== null && item.changePercent !== undefined) {
          const rounded = Math.round(item.changePercent)
          if (rounded > 0) {
            badge = `<span class="badge-up">↑ ${Math.abs(rounded)}%</span>`
          } else if (rounded < 0) {
            badge = `<span class="badge-down">↓ ${Math.abs(rounded)}%</span>`
          } else {
            badge = '<span class="badge-neutral">0%</span>'
          }
        }

        const color = getServiceColor(item.name, idx)
        const barWidth = Math.min(Math.round(item.share), 100)

        return `
        <tr>
          <td class="text-center text-muted font-mono">${idx + 1}</td>
          <td>
            <span class="color-dot" style="background: ${color};"></span>
            <span class="font-semibold text-slate-800">${item.name}</span>
          </td>
          <td class="text-right font-mono text-muted">${item.prevCost !== null && item.prevCost > 0 ? formatCurrency(item.prevCost) : "—"}</td>
          <td class="text-right font-mono font-bold text-slate-900">${formatCurrency(item.cost)}</td>
          <td class="text-center">${badge}</td>
          <td class="text-right">
            <div class="bar-wrap">
              <span class="font-mono font-medium" style="min-width: 38px; text-align: right;">${item.share.toFixed(1)}%</span>
              <div class="mini-bar-bg"><div class="mini-bar-fill" style="width: ${barWidth}%; background: ${color};"></div></div>
            </div>
          </td>
        </tr>`
      })
      .join("")

    const scopeTitle = isSummaryPage
      ? (effectiveProjects.length > 1
          ? `Combined Summary (${effectiveProjects.length} Projects)`
          : "Entire Billing Account")
      : `Project: ${pageData.projectId}`

    // Project breakdown table on Summary page
    const projectContributionSection =
      isSummaryPage && contributions.length > 1
        ? `
        <div class="table-box" style="margin-bottom: 11px;">
          <div class="table-head-bar">
            <div class="sec-title">Project Contribution Breakdown</div>
            <div class="sec-sub">Summary comparison across all ${contributions.length} selected projects</div>
          </div>
          <table>
            <thead>
              <tr>
                <th class="text-center" style="width: 25px;">#</th>
                <th>Project ID</th>
                <th class="text-right" style="width: 140px;">Total Net Cost</th>
                <th class="text-right" style="width: 120px;">Contribution Share</th>
              </tr>
            </thead>
            <tbody>
              ${contributions
                .map((c, idx) => `
                <tr>
                  <td class="text-center text-muted font-mono">${idx + 1}</td>
                  <td class="font-semibold text-slate-800">${c.projectId || "[Unallocated]"}</td>
                  <td class="text-right font-mono font-bold text-slate-900">${formatCurrency(c.cost)}</td>
                  <td class="text-right font-mono font-semibold text-blue-700">${c.share.toFixed(1)}%</td>
                </tr>
              `)
                .join("")}
            </tbody>
          </table>
        </div>`
        : ""

    return `
      <div class="report-wrap page-break">
        <!-- Header -->
        <div class="header">
          <div class="brand-box">
            ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" class="brand-logo" />` : '<div class="brand-title">MagnaSight</div>'}
            <div>
              <div class="brand-title">${logoBase64 ? "MagnaSight" : ""}</div>
              <div class="brand-sub">Cloud Usage & Cost Intelligence Report</div>
            </div>
          </div>
          <div class="meta-box">
            <div class="report-title">${isSummaryPage ? "Monthly Summary Invoice" : "Project Usage Report"}</div>
            <div class="meta-line">${resolvedCustomerName ? `<strong>Customer:</strong> ${resolvedCustomerName} &nbsp;|&nbsp; ` : ""}<strong>Scope:</strong> ${scopeTitle} &nbsp;|&nbsp; <strong>Period:</strong> ${pageData.dateRange || monthYear}</div>
            <div class="meta-line"><strong>Billing Account:</strong> ${selectedBillingId || "Default"} &nbsp;|&nbsp; <strong>Generated:</strong> ${generatedTimestamp} &nbsp;|&nbsp; <strong>Page:</strong> ${pageNumber} of ${totalPages}</div>
          </div>
        </div>

        <!-- 4 KPI Cards -->
        <div class="kpi-grid">
          <div class="kpi-card">
            <div class="kpi-label">Total Net Cost</div>
            <div class="kpi-value">${formatCurrency(pageData.totalCost)}</div>
            <div class="kpi-sub">${momBadgeOverall} vs ${pageData.prevMonthLabel}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Credits & Savings</div>
            <div class="kpi-value" style="color: #16a34a;">${formatCurrency(pageData.totalSavings)}</div>
            <div class="kpi-sub">List Price: ${formatCurrency(pageData.totalGross)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Top Cost Driver</div>
            <div class="kpi-value" style="font-size: 13.5px; line-height: 1.4; padding: 2px 0 1px 0; white-space: nowrap;">${pageData.topService ? (pageData.topService.name.length > 20 ? pageData.topService.name.slice(0, 18) + '...' : pageData.topService.name) : '—'}</div>
            <div class="kpi-sub">${pageData.topService ? `${formatCurrency(pageData.topService.cost)} (${pageData.topService.share.toFixed(1)}% share)` : "—"}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Active Services</div>
            <div class="kpi-value">${pageData.activeServicesCount} <span style="font-size: 11px; font-weight: 500; color: #64748b;">/ ${pageData.serviceCount} Total</span></div>
            <div class="kpi-sub">Registered cloud services</div>
          </div>
        </div>

        <!-- Daily Trend Chart -->
        ${
          chartImg
            ? `
        <div class="chart-box">
          <div class="sec-head">
            <div class="sec-title">Daily Billing Cost Trend</div>
            <div class="sec-sub">Daily net expenditure breakdown for ${monthYear} (IDR)</div>
          </div>
          <img src="${chartImg}" alt="Daily Cost Chart" class="chart-img" />
        </div>`
            : ""
        }

        <!-- Project contribution section if on summary page -->
        ${projectContributionSection}

        <!-- Service Table -->
        <div class="table-box">
          <div class="table-head-bar">
            <div class="sec-title">Service Usage Breakdown & MoM Performance</div>
            <div class="sec-sub">Ranked by current expenditure</div>
          </div>
          <table>
            <thead>
              <tr>
                <th class="text-center" style="width: 25px;">#</th>
                <th>Service Name</th>
                <th class="text-right" style="width: 120px;">${pageData.prevMonthLabel} (Prev)</th>
                <th class="text-right" style="width: 125px;">${pageData.currMonthLabel} (Current)</th>
                <th class="text-center" style="width: 80px;">MoM Change</th>
                <th class="text-right" style="width: 110px;">Share of Total</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
            <tfoot>
              <tr class="tfoot-tr">
                <td class="text-center">Σ</td>
                <td>Total (${pageData.serviceCount} Services)</td>
                <td class="text-right font-mono text-muted">${pageData.prevTotalCost !== null ? formatCurrency(pageData.prevTotalCost) : "—"}</td>
                <td class="text-right font-mono font-bold" style="color: #1e3a8a;">${formatCurrency(pageData.totalCost)}</td>
                <td class="text-center">${momBadgeOverall}</td>
                <td class="text-right font-mono">100.0%</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Insights Box -->
        <div class="insights-box">
          <div class="sec-head" style="margin-bottom: 4px;">
            <div class="sec-title" style="color: #1e3a8a;">Kesimpulan & Rekomendasi Efisiensi Biaya</div>
            <div class="sec-sub">Executive Insights & Cost Optimization Action Items</div>
          </div>
          <div class="insights-summary">
            ${summarySentence} ${highlightText}
          </div>
          <div class="recom-grid">
            <div class="recom-card">
              <div class="recom-title">⚡ Rightsizing & Scheduling VM</div>
              <div class="recom-desc">
                Audit pemakaian vCPU & RAM pada <strong>Compute Engine</strong>. Lakukan rightsizing pada instance underutilized (&lt;15%) dan terapkan <em>Instance Scheduler</em> untuk VM non-produksi di luar jam operasional.
              </div>
            </div>
            <div class="recom-card">
              <div class="recom-title">🗑️ Pembersihan Resource Idle</div>
              <div class="recom-desc">
                Periksa dan hapus resource yang sudah tidak digunakan seperti <em>Unattached Persistent Disks</em>, <em>Idle Static IPs</em>, dan snapshot usang untuk mencegah timbulnya biaya residual tanpa utilisasi riil.
              </div>
            </div>
            <div class="recom-card">
              <div class="recom-title">📉 Komitmen CUD & Storage Tier</div>
              <div class="recom-desc">
                Pertimbangkan <em>Committed Use Discounts (CUD)</em> 1-3 tahun untuk workload stabil (diskon s/d 57%). Aktifkan <em>Object Lifecycle Management</em> pada Cloud Storage ke Nearline/Coldline.
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="footer">
          <div>Generated by MagnaSight Cloud Intelligence System</div>
          <div>Source: Google Cloud Platform Billing Export API</div>
          <div>© ${new Date().getFullYear()} MagnaSight. Confidential & Proprietary.</div>
        </div>
      </div>
    `
  }

  const getInformativeFileName = (month, custName, billingId) => {
    const dateObj = month ? new Date(`${month}-01`) : new Date()
    const monthYear = format(dateObj, "MMMM yyyy")
    const cleanCustomer = custName ? custName.replace(/[/\\?%*:|"<>]/g, "-").trim() : ""

    if (cleanCustomer) {
      return `Usage Report ${cleanCustomer} - ${monthYear}.pdf`
    }
    if (billingId) {
      return `Usage Report ${billingId} - ${monthYear}.pdf`
    }
    return `Usage Report - ${monthYear}.pdf`
  }

  // Handle Multi-page direct PDF download
  const handleDownloadPDF = async () => {
    if (!reportPayload || !reportPayload.allPages || reportPayload.allPages.length === 0 || isDownloading) return
    setIsDownloading(true)
    const fileName = getInformativeFileName(selectedMonth, resolvedCustomerName, selectedBillingId)

    // Generate chart base64 image and HTML for each page
    const totalPages = reportPayload.allPages.length
    const pagesHTMLList = reportPayload.allPages.map((pageData, index) => {
      const pageChartImg = renderChartToBase64(pageData.dailyChartData)
      const isSummary =
        reportPayload.isMultiProject
          ? index === totalPages - 1
          : effectiveProjects.length === 0
      return generatePageHTML(
        pageData,
        pageChartImg,
        isSummary,
        index + 1,
        totalPages,
        reportPayload.projectContributions || []
      )
    })

    const cssStyles = `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body, .report-wrap {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        color: #0f172a;
        background: #ffffff;
        font-size: 11px;
        line-height: 1.4;
        width: 794px;
        min-height: 1123px;
        padding: 24px 30px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      .header { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 8px; border-bottom: 2.5px solid #2563eb; margin-bottom: 11px; }
      .brand-box { display: flex; align-items: center; gap: 10px; }
      .brand-logo { height: 28px; width: auto; object-fit: contain; }
      .brand-title { font-size: 18px; font-weight: 800; color: #1e3a8a; letter-spacing: -0.4px; }
      .brand-sub { font-size: 9.5px; color: #64748b; margin-top: 1px; font-weight: 500; }
      .meta-box { text-align: right; }
      .report-title { font-size: 14px; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 0.5px; }
      .meta-line { font-size: 10px; color: #475569; margin-top: 2px; }
      .meta-line strong { color: #0f172a; }
      .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 12px; }
      .kpi-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; min-height: 72px; display: flex; flex-direction: column; justify-content: space-between; }
      .kpi-label { font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; line-height: 1.3; }
      .kpi-value { font-size: 15px; font-weight: 800; color: #0f172a; line-height: 1.4; padding: 2px 0 1px 0; }
      .kpi-sub { font-size: 9px; color: #64748b; margin-top: 3px; font-weight: 500; line-height: 1.3; }
      .badge-up { color: #dc2626; font-weight: 700; background: #fef2f2; padding: 2px 6px; border-radius: 4px; display: inline-block; font-size: 9px; }
      .badge-down { color: #16a34a; font-weight: 700; background: #f0fdf4; padding: 2px 6px; border-radius: 4px; display: inline-block; font-size: 9px; }
      .badge-new { color: #2563eb; font-weight: 700; background: #eff6ff; padding: 2px 6px; border-radius: 4px; display: inline-block; font-size: 9px; }
      .badge-neutral { color: #64748b; font-weight: 600; background: #f1f5f9; padding: 2px 6px; border-radius: 4px; display: inline-block; font-size: 9px; }
      .chart-box { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; margin-bottom: 11px; }
      .sec-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
      .sec-title { font-size: 11.5px; font-weight: 700; color: #1e293b; text-transform: uppercase; letter-spacing: 0.4px; }
      .sec-sub { font-size: 9.5px; color: #64748b; }
      .chart-img { width: 100%; max-height: 165px; object-fit: contain; display: block; }
      .table-box { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; margin-bottom: 11px; }
      .table-head-bar { padding: 8px 12px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
      table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
      th { background: #f1f5f9; color: #334155; font-weight: 700; font-size: 9px; text-transform: uppercase; letter-spacing: 0.4px; padding: 6.5px 10px; border-bottom: 1px solid #cbd5e1; text-align: left; }
      td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; color: #1e293b; vertical-align: middle; }
      tr:nth-child(even) { background-color: #fafbfc; }
      .text-right { text-align: right; }
      .text-center { text-align: center; }
      .font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace; }
      .font-bold { font-weight: 700; }
      .font-semibold { font-weight: 600; }
      .font-medium { font-weight: 500; }
      .text-muted { color: #64748b; }
      .color-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
      .bar-wrap { display: flex; align-items: center; justify-content: flex-end; gap: 6px; }
      .mini-bar-bg { width: 48px; height: 5px; background: #e2e8f0; border-radius: 3px; overflow: hidden; }
      .mini-bar-fill { height: 100%; border-radius: 3px; }
      .tfoot-tr td { background: #f8fafc; border-top: 1.5px solid #cbd5e1; font-weight: 700; font-size: 10.5px; padding: 7px 10px; }
      .insights-box { background: #f8fafc; border: 1px solid #cbd5e1; border-left: 3.5px solid #2563eb; border-radius: 6px; padding: 7px 10px; margin-bottom: 7px; }
      .insights-summary { font-size: 9px; color: #334155; margin-bottom: 6px; line-height: 1.4; }
      .recom-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
      .recom-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 4px; padding: 5px 7px; }
      .recom-title { font-size: 8.5px; font-weight: 700; color: #1e3a8a; margin-bottom: 2px; display: flex; align-items: center; gap: 3px; }
      .recom-desc { font-size: 8px; color: #475569; line-height: 1.35; }
      .footer { margin-top: auto; padding-top: 6px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 8.5px; color: #94a3b8; }
    `

    try {
      // Offscreen container to hold the page for canvas snapshot
      const container = document.createElement("div")
      container.style.position = "fixed"
      container.style.left = "-9999px"
      container.style.top = "0"
      container.style.width = "794px"
      container.style.backgroundColor = "#ffffff"
      container.style.zIndex = "-9999"
      container.style.opacity = "1"
      container.style.pointerEvents = "none"

      const styleEl = document.createElement("style")
      styleEl.innerHTML = cssStyles
      container.appendChild(styleEl)
      document.body.appendChild(container)

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      })

      for (let i = 0; i < pagesHTMLList.length; i++) {
        const pageWrapper = document.createElement("div")
        pageWrapper.innerHTML = pagesHTMLList[i]
        container.appendChild(pageWrapper)

        // Allow microtask tick for DOM rendering & chart display
        await new Promise((r) => setTimeout(r, 60))

        const canvas = await html2canvas(pageWrapper, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          windowWidth: 794,
        })

        const imgData = canvas.toDataURL("image/jpeg", 0.95)

        if (i > 0) {
          doc.addPage("a4", "portrait")
        }
        doc.addImage(imgData, "JPEG", 0, 0, 210, 297, undefined, "FAST")

        container.removeChild(pageWrapper)
      }

      document.body.removeChild(container)

      // Direct download save without opening a new tab
      doc.save(fileName)
    } catch (err) {
      console.warn("Direct jsPDF export error, falling back to hidden iframe:", err)
      const iframe = document.createElement("iframe")
      iframe.style.position = "fixed"
      iframe.style.right = "0"
      iframe.style.bottom = "0"
      iframe.style.width = "0"
      iframe.style.height = "0"
      iframe.style.border = "none"
      document.body.appendChild(iframe)

      const fullHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${fileName.replace(".pdf", "")}</title>
            <style>
              @page { size: A4 portrait; margin: 8mm 10mm; }
              ${cssStyles}
              .page-break { page-break-after: always; break-after: page; }
              .page-break:last-child { page-break-after: avoid; break-after: avoid; }
            </style>
          </head>
          <body>
            ${pagesHTMLList.join("\n")}
          </body>
        </html>
      `
      iframe.contentDocument.write(fullHTML)
      iframe.contentDocument.close()
      setTimeout(() => {
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
        setTimeout(() => {
          if (document.body.contains(iframe)) document.body.removeChild(iframe)
        }, 3000)
      }, 400)
    } finally {
      setIsDownloading(false)
    }
  }

  if (!isOpen || !selectedMonth) return null

  // Active page data for the in-modal interactive preview
  const currentModalPage = reportPayload
    ? activeTab === "summary" || !reportPayload.isMultiProject
      ? reportPayload.summaryPage || reportPayload.projectPages[0]
      : reportPayload.projectPages.find((p) => p.projectId === activeTab) || reportPayload.summaryPage
    : null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-sans">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Top Header */}
        <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-indigo-800 text-white px-6 py-4 flex flex-wrap justify-between items-center gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/15 p-2 rounded-xl backdrop-blur-sm shadow-xs">
              <img src={logoBase64 || "/logo.png"} alt="MagnaSight Logo" className="h-6 w-auto object-contain" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold tracking-tight">Invoice & Usage Report</h2>
                {reportPayload?.isMultiProject && (
                  <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-indigo-500/30 text-indigo-100 border border-indigo-400/30">
                    {reportPayload.allPages.length} Pages
                  </span>
                )}
              </div>
              <p className="text-blue-100 text-xs font-medium mt-0.5">
                {format(new Date(`${selectedMonth}-01`), "MMMM yyyy")} •{" "}
                {resolvedCustomerName ? `${resolvedCustomerName} • ` : ""}
                {reportPayload?.isMultiProject
                  ? `${effectiveProjects.length} Selected Projects + Combined Summary`
                  : effectiveProjects.length === 1
                  ? `Project: ${effectiveProjects[0]}`
                  : `Entire Billing Account (${selectedBillingId || "Default"})`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleDownloadPDF}
              disabled={!reportPayload || isLoading || isDownloading}
              className="px-4 py-2 bg-white hover:bg-blue-50 text-blue-700 font-semibold rounded-xl shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-xs sm:text-sm transition-all cursor-pointer"
              title="Download PDF directly"
            >
              {isDownloading ? (
                <>
                  <FaSpinner className="animate-spin w-4 h-4 text-blue-700" />
                  <span>Downloading PDF...</span>
                </>
              ) : (
                <>
                  <FiDownload className="w-4 h-4" />
                  <span>
                    Download PDF {reportPayload?.allPages?.length > 1 ? `(${reportPayload.allPages.length} Pages)` : ""}
                  </span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors flex items-center justify-center cursor-pointer"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Multi-Project Preview Navigation Tabs (When 2+ Projects Selected) */}
        {reportPayload?.isMultiProject && (
          <div className="bg-slate-100/90 border-b border-slate-200 px-6 py-2.5 flex items-center gap-2 overflow-x-auto shrink-0">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mr-1 flex items-center gap-1">
              <FiLayers className="text-indigo-600" />
              <span>Pages:</span>
            </span>

            {/* Tab: Combined Summary */}
            <button
              type="button"
              onClick={() => setActiveTab("summary")}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shrink-0 ${
                activeTab === "summary"
                  ? "bg-indigo-600 text-white shadow-xs"
                  : "bg-white text-slate-700 hover:bg-slate-200 border border-slate-200"
              }`}
            >
              <FiPieChart className="w-3.5 h-3.5" />
              <span>Summary Gabungan ({effectiveProjects.length} Projects)</span>
            </button>

            {/* Tabs: Individual Projects */}
            {effectiveProjects.map((proj) => (
              <button
                key={proj}
                type="button"
                onClick={() => setActiveTab(proj)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition flex items-center gap-1.5 cursor-pointer shrink-0 ${
                  activeTab === proj
                    ? "bg-blue-600 text-white shadow-xs"
                    : "bg-white text-slate-600 hover:bg-slate-200 border border-slate-200"
                }`}
              >
                <FiFolder className="w-3.5 h-3.5 text-slate-400" />
                <span>{proj}</span>
              </button>
            ))}
          </div>
        )}

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 bg-slate-50/60">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3"></div>
              <span className="text-base font-semibold text-slate-700">Loading invoice & usage report...</span>
              <span className="text-xs text-slate-500 mt-0.5">Fetching and calculating monthly cost breakdown</span>
            </div>
          ) : currentModalPage ? (
            <div className="space-y-5">
              {/* Scope Title Badge inside content */}
              <div className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-xl px-4 py-2.5 border border-slate-200 shadow-2xs">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-slate-500 uppercase tracking-wider">Previewing:</span>
                  <span className="font-bold text-slate-900 bg-slate-100 px-2.5 py-1 rounded-lg">
                    {currentModalPage.pageTitle}
                  </span>
                </div>
                {reportPayload?.isMultiProject && (
                  <span className="text-xs text-indigo-600 font-medium">
                    {activeTab === "summary"
                      ? "Summary Page (Will print as last page)"
                      : `Individual Project Page (Will print as page ${effectiveProjects.indexOf(activeTab) + 1} of ${reportPayload.allPages.length})`}
                  </span>
                )}
              </div>

              {/* 4 KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                <div className="bg-white rounded-xl p-4 border border-slate-200/80 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="p-1.5 bg-blue-50 rounded-md">
                      <FiDollarSign className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Net Cost</span>
                  </div>
                  <p className="text-xl font-extrabold text-slate-900">{formatCurrency(currentModalPage.totalCost)}</p>
                  {currentModalPage.overallChangePercent !== null && (
                    <p
                      className={`text-xs font-semibold mt-1 flex items-center gap-1 ${
                        currentModalPage.overallChangePercent > 0 ? "text-red-600" : "text-emerald-600"
                      }`}
                    >
                      {currentModalPage.overallChangePercent > 0 ? "↑" : "↓"} {Math.abs(Math.round(currentModalPage.overallChangePercent))}%{" "}
                      <span className="text-slate-400 font-normal">vs {currentModalPage.prevMonthLabel}</span>
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-xl p-4 border border-slate-200/80 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="p-1.5 bg-emerald-50 rounded-md">
                      <FiShield className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Credits & Savings</span>
                  </div>
                  <p className="text-xl font-extrabold text-emerald-600">{formatCurrency(currentModalPage.totalSavings)}</p>
                  <p className="text-xs text-slate-400 font-medium mt-1">List: {formatCurrency(currentModalPage.totalGross)}</p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-slate-200/80 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="p-1.5 bg-indigo-50 rounded-md">
                      <FiLayers className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Top Cost Driver</span>
                  </div>
                  <p className="text-base font-bold text-slate-900 truncate" title={currentModalPage.topService?.name}>
                    {currentModalPage.topService ? currentModalPage.topService.name : "—"}
                  </p>
                  <p className="text-xs text-slate-500 font-medium mt-1">
                    {currentModalPage.topService
                      ? `${formatCurrency(currentModalPage.topService.cost)} (${currentModalPage.topService.share.toFixed(1)}%)`
                      : "—"}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-4 border border-slate-200/80 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="p-1.5 bg-purple-50 rounded-md">
                      <FiCalendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Services</span>
                  </div>
                  <p className="text-xl font-extrabold text-slate-900">
                    {currentModalPage.activeServicesCount}{" "}
                    <span className="text-xs font-medium text-slate-500">/ {currentModalPage.serviceCount} Total</span>
                  </p>
                  <p className="text-xs text-slate-400 font-medium mt-1">Services in period</p>
                </div>
              </div>

              {/* Multi-Project Breakdown Table (Summary Tab Only) */}
              {reportPayload?.isMultiProject && activeTab === "summary" && reportPayload.projectContributions && (
                <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
                      Project Contribution Breakdown
                    </h3>
                    <span className="text-xs text-slate-500 font-medium">
                      Summary across all {reportPayload.projectContributions.length} selected projects
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-bold tracking-wider border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-center w-12">#</th>
                          <th className="px-4 py-3">Project ID</th>
                          <th className="px-4 py-3 text-right">Total Net Cost</th>
                          <th className="px-4 py-3 text-right w-36">Contribution Share</th>
                          <th className="px-4 py-3 text-center w-28">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {reportPayload.projectContributions.map((c, idx) => (
                          <tr key={c.projectId || idx} className="hover:bg-slate-50/70 transition">
                            <td className="px-4 py-2.5 text-center font-mono text-slate-400 text-xs">{idx + 1}</td>
                            <td className="px-4 py-2.5 font-bold text-slate-800">{c.projectId || "[Unallocated]"}</td>
                            <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900">
                              {formatCurrency(c.cost)}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono font-semibold text-indigo-700">
                              {c.share.toFixed(1)}%
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <button
                                type="button"
                                onClick={() => setActiveTab(c.projectId)}
                                className="text-xs text-blue-600 hover:underline font-semibold cursor-pointer"
                              >
                                View Details →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Chart Section */}
              {currentModalPage?.dailyChartData && (
                <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Daily Billing Cost Trend</h3>
                    <span className="text-xs text-slate-500">
                      {format(new Date(`${selectedMonth}-01`), "MMMM yyyy")} (IDR)
                    </span>
                  </div>
                  <div className="h-56 bg-slate-50/50 rounded-lg p-2 border border-slate-100">
                    <Bar
                      key={`${activeTab}-${selectedMonth}`}
                      ref={previewChartRef}
                      data={currentModalPage.dailyChartData}
                      options={chartOptions}
                    />
                  </div>
                </div>
              )}

              {/* Table Section */}
              <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                    Service Usage Breakdown & MoM Performance
                  </h3>
                  <span className="text-xs text-slate-500">Ranked by current expenditure</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase font-bold tracking-wider border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-center w-12">#</th>
                        <th className="px-4 py-3">Service Name</th>
                        <th className="px-4 py-3 text-right">{currentModalPage.prevMonthLabel} (Prev)</th>
                        <th className="px-4 py-3 text-right">{currentModalPage.currMonthLabel} (Current)</th>
                        <th className="px-4 py-3 text-center w-28">MoM Change</th>
                        <th className="px-4 py-3 text-right w-36">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {currentModalPage.serviceBreakdown.map((item, index) => {
                        const color = getServiceColor(item.name, index)
                        const barWidth = Math.min(Math.round(item.share), 100)
                        return (
                          <tr key={item.name} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-4 py-2.5 text-center text-xs font-mono text-slate-400">{index + 1}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }}></span>
                                <span className="font-semibold text-slate-800">{item.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono text-slate-500 text-xs">
                              {item.prevCost !== null && item.prevCost > 0 ? formatCurrency(item.prevCost) : "—"}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-900 text-xs">
                              {formatCurrency(item.cost)}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              {item.isNew ? (
                                <span className="inline-block text-[11px] font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                                  NEW
                                </span>
                              ) : item.changePercent !== null && item.changePercent !== undefined ? (
                                <span
                                  className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded ${
                                    item.changePercent > 0
                                      ? "bg-red-50 text-red-600"
                                      : item.changePercent < 0
                                      ? "bg-emerald-50 text-emerald-600"
                                      : "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {item.changePercent > 0 ? "↑" : item.changePercent < 0 ? "↓" : ""}{" "}
                                  {Math.abs(Math.round(item.changePercent))}%
                                </span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <span className="font-mono text-xs font-medium text-slate-600 w-11 text-right">
                                  {item.share.toFixed(1)}%
                                </span>
                                <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full"
                                    style={{ width: `${barWidth}%`, backgroundColor: color }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-bold text-slate-900">
                      <tr>
                        <td className="px-4 py-3 text-center text-xs">Σ</td>
                        <td className="px-4 py-3">Total (${currentModalPage.serviceCount} Services)</td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-slate-500">
                          {currentModalPage.prevTotalCost !== null ? formatCurrency(currentModalPage.prevTotalCost) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-blue-900">
                          {formatCurrency(currentModalPage.totalCost)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {currentModalPage.overallChangePercent !== null && (
                            <span
                              className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded ${
                                currentModalPage.overallChangePercent > 0
                                  ? "bg-red-50 text-red-600"
                                  : "bg-emerald-50 text-emerald-600"
                              }`}
                            >
                              {currentModalPage.overallChangePercent > 0 ? "↑" : "↓"}{" "}
                              {Math.abs(Math.round(currentModalPage.overallChangePercent))}%
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs">100.0%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Kesimpulan & Rekomendasi Section */}
              <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                    Kesimpulan & Rekomendasi Efisiensi Biaya
                  </h3>
                  <span className="text-xs text-slate-500">Executive Insights & Cost Optimization</span>
                </div>

                <div className="p-3.5 bg-blue-50/60 rounded-xl border border-blue-100/80 text-xs text-slate-700 leading-relaxed space-y-1">
                  <p>
                    {currentModalPage.isOngoing ? (
                      `Periode ini masih berjalan (${currentModalPage.dateRange}). Akumulasi pengeluaran cloud hingga saat ini sebesar ${formatCurrency(currentModalPage.totalCost)}.`
                    ) : currentModalPage.overallChangePercent !== null ? (
                      <>
                        Total pengeluaran cloud periode ini sebesar <strong>{formatCurrency(currentModalPage.totalCost)}</strong>,{" "}
                        {currentModalPage.overallChangePercent > 0 ? (
                          <>mengalami kenaikan sebesar <strong className="text-red-600">{formatCurrency(currentModalPage.overallDelta)} (↑ {Math.abs(Math.round(currentModalPage.overallChangePercent))}%)</strong></>
                        ) : currentModalPage.overallChangePercent < 0 ? (
                          <>berhasil mengalami penghematan/penurunan sebesar <strong className="text-emerald-600">{formatCurrency(Math.abs(currentModalPage.overallDelta))} (↓ {Math.abs(Math.round(currentModalPage.overallChangePercent))}%)</strong></>
                        ) : (
                          "stabil"
                        )}{" "}
                        dibandingkan {currentModalPage.prevMonthLabel}.
                      </>
                    ) : (
                      `Total pengeluaran cloud periode ini tercatat sebesar ${formatCurrency(currentModalPage.totalCost)}.`
                    )}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
                  <div className="bg-slate-50/70 border border-slate-200/70 rounded-xl p-3.5">
                    <div className="text-xs font-bold text-blue-900 mb-1 flex items-center gap-1.5">
                      <span>⚡</span> Rightsizing & Scheduling VM
                    </div>
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Audit utilisasi vCPU & RAM pada <strong>Compute Engine</strong>. Terapkan rightsizing pada instance underutilized (&lt;15%) dan terapkan <em>Instance Scheduler</em> untuk VM non-produksi di luar jam kerja.
                    </p>
                  </div>

                  <div className="bg-slate-50/70 border border-slate-200/70 rounded-xl p-3.5">
                    <div className="text-xs font-bold text-amber-900 mb-1 flex items-center gap-1.5">
                      <span>🗑️</span> Pembersihan Resource Idle
                    </div>
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Periksa dan hapus resource yang sudah tidak digunakan seperti <em>Unattached Persistent Disks</em>, <em>Idle Static IPs</em>, dan snapshot usang untuk mencegah timbulnya biaya residual tanpa utilisasi riil.
                    </p>
                  </div>

                  <div className="bg-slate-50/70 border border-slate-200/70 rounded-xl p-3.5">
                    <div className="text-xs font-bold text-emerald-900 mb-1 flex items-center gap-1.5">
                      <span>📉</span> Komitmen CUD & Storage Tier
                    </div>
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Pertimbangkan <em>Committed Use Discounts (CUD)</em> 1-3 tahun untuk workload stabil (diskon s/d 57%). Aktifkan <em>Object Lifecycle Management</em> pada Cloud Storage ke Nearline/Coldline.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-base font-semibold text-slate-600">No usage data found for this period</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
